// FILE: src/admin/pages/support/owners/tabs/SupportOwnerRidesTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  listSupportOwnerRides,
  getSupportOwnerRideDetail,
  getAdminSupportBookingDetail,
} from "../../../../../api/adminSupportApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safeStr(x) {
  return x == null ? "" : String(x).trim();
}

function up(x) {
  return safeStr(x).toUpperCase();
}

function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Consistent date-time: "7 Feb 2026, 12:12 PM" (Asia/Kolkata)
 * Accepts ISO string, Instant string, Date-like string.
 */
function dt(v) {
  const s = safeStr(v);
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
  } catch (_) {}
  // fallback: keep readable
  return s.replace("T", " ").replace("+05:30", "").replace("Z", "").slice(0, 19);
}

function Chip({ children, tone = "neutral" }) {
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-900"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-800"
      : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <span
      className={classNames(
        "inline-flex items-center px-3 py-1.5 text-xs font-semibold border rounded-md",
        cls
      )}
    >
      {children}
    </span>
  );
}

function toneForRideStatus(s) {
  const v = up(s);
  if (v === "ENDED" || v === "COMPLETED") return "good";
  if (v === "CANCELLED" || v === "FAILED") return "bad";
  if (v === "RUNNING" || v === "STARTED" || v === "ARRIVED") return "warn";
  return "neutral";
}

function toneForBookingStatus(s) {
  const v = up(s);
  if (v === "COMPLETED") return "good";
  if (v === "CANCELLED" || v === "UNFULFILLED") return "bad";
  if (v === "CONFIRMED") return "warn";
  return "neutral";
}

function toneForPaymentStatus(s) {
  const v = up(s);
  if (v === "PAID" || v === "CAPTURED") return "good";
  if (v === "FAILED" || v === "CANCELLED") return "bad";
  if (v === "CREATED" || v === "PENDING") return "warn";
  return "neutral";
}

function PaginationFooter({
  page,
  totalPages,
  size,
  onSize,
  onPrev,
  onNext,
  totalElements,
}) {
  return (
    <div className="px-5 py-3 border-t border-slate-200 bg-white flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="text-xs text-slate-500">
        Page{" "}
        <span className="font-semibold text-slate-700">{page + 1}</span> of{" "}
        <span className="font-semibold text-slate-700">
          {Math.max(1, totalPages)}
        </span>
        {Number.isFinite(Number(totalElements)) ? (
          <>
            {" "}
            <span className="text-slate-300">•</span>{" "}
            <span className="font-semibold text-slate-700">
              {Number(totalElements).toLocaleString("en-IN")}
            </span>{" "}
            total
          </>
        ) : null}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-500">View</div>
          <div className="relative">
            <select
              className="appearance-none h-10 rounded-md border border-slate-200 bg-white pl-3 pr-9 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              value={size}
              onChange={(e) => onSize(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
              ▼
            </span>
          </div>
        </div>

        <div className="text-sm text-slate-700">
          Page <span className="font-semibold">{page + 1}</span> of{" "}
          <span className="font-semibold">{Math.max(1, totalPages)}</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            onClick={onPrev}
            disabled={page <= 0}
          >
            Prev
          </button>
          <button
            className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            onClick={onNext}
            disabled={page + 1 >= totalPages}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="p-3 rounded-lg border border-slate-200">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-900">{value ?? "—"}</div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="mt-5">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          {subtitle ? <div className="text-xs text-slate-500">{subtitle}</div> : null}
        </div>
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

export default function SupportOwnerRidesTab() {
  const { ownerId } = useParams();

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(30);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [openRideId, setOpenRideId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // optional: booking detail drawer (admin support booking detail)
  const [openBookingId, setOpenBookingId] = useState(null);
  const [bookingDetail, setBookingDetail] = useState(null);
  const [bookingDetailLoading, setBookingDetailLoading] = useState(false);

  const totalPages = data?.totalPages ?? 1;
  const totalElements = data?.totalElements ?? undefined;
  const rows = data?.content ?? [];

  const queryKey = useMemo(
    () => `${ownerId}|${q}|${status}|${page}|${size}`,
    [ownerId, q, status, page, size]
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);

    listSupportOwnerRides({
      ownerId,
      q: safeStr(q) || undefined,
      status: status || undefined,
      page,
      size,
    })
      .then((res) => alive && setData(res))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [queryKey, ownerId]);

  useEffect(() => {
    if (!openRideId) return;
    let alive = true;
    setDetail(null);
    setDetailLoading(true);

    getSupportOwnerRideDetail(ownerId, openRideId)
      .then((res) => alive && setDetail(res))
      .finally(() => alive && setDetailLoading(false));

    return () => {
      alive = false;
    };
  }, [openRideId, ownerId]);

  useEffect(() => {
    if (!openBookingId) return;
    let alive = true;
    setBookingDetail(null);
    setBookingDetailLoading(true);

    getAdminSupportBookingDetail(openBookingId)
      .then((res) => alive && setBookingDetail(res))
      .finally(() => alive && setBookingDetailLoading(false));

    return () => {
      alive = false;
    };
  }, [openBookingId]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {/* controls */}
      <div className="p-5 border-b border-slate-200 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Owner Rides</div>
          <div className="text-xs text-slate-500">
            Search rides and open full context in drawer (ride + booking + request + offer)
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            className="h-10 w-full sm:w-80 rounded-md border border-slate-200 bg-white px-3 text-sm"
            placeholder="Search: rideId, bookingId, regNo, user phone/email..."
            value={q}
            onChange={(e) => {
              setPage(0);
              setQ(e.target.value);
            }}
          />

          <select
            className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700"
            value={status}
            onChange={(e) => {
              setPage(0);
              setStatus(e.target.value);
            }}
          >
            <option value="">All ride statuses</option>
            <option value="RUNNING">RUNNING</option>
            <option value="STARTED">STARTED</option>
            <option value="ARRIVED">ARRIVED</option>
            <option value="ENDED">ENDED</option>
            <option value="CANCELLED">CANCELLED</option>
          </select>
        </div>
      </div>

      {/* table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">Ride</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-left px-5 py-3 font-semibold">Booking</th>
              <th className="text-left px-5 py-3 font-semibold">Vehicle</th>
              <th className="text-left px-5 py-3 font-semibold">Driver</th>
              <th className="text-left px-5 py-3 font-semibold">Schedule</th>
              <th className="text-left px-5 py-3 font-semibold">Updated</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td className="px-5 py-6 text-slate-500" colSpan={7}>
                  Loading…
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td className="px-5 py-6 text-slate-500" colSpan={7}>
                  No rides found.
                </td>
              </tr>
            )}

            {!loading &&
              rows.map((r) => {
                const rideId = r.rideId ?? r.id;
                const bookingId = r.bookingId;
                const status = r.status;
                const vehicleRegNo = r.vehicleRegNo ?? r.vehicleReg ?? r.regNo;
                const driverName = safeStr(r.driverName) || "—";
                const driverPhone = safeStr(r.driverPhone);
                const driverCell = driverPhone ? `${driverName} (${driverPhone})` : driverName;

                // unified schedule (prefer booking schedule fields present on row)
                const pickupAt = r.pickupAt || r.pickupDateTime || null;
                const dropAt = r.dropAt || r.dropDateTime || null;
                const schedule = pickupAt || dropAt ? `${dt(pickupAt)} → ${dt(dropAt)}` : "—";

                const updatedAt = r.updatedAt ?? r.createdAt;

                return (
                  <tr
                    key={rideId}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => setOpenRideId(rideId)}
                  >
                    <td className="px-5 py-3 text-slate-900">#{rideId}</td>

                    <td className="px-5 py-3">
                      <Chip tone={toneForRideStatus(status)}>{safeStr(status) || "—"}</Chip>
                    </td>

                    <td className="px-5 py-3 text-slate-700">
                      {bookingId ? `#${bookingId}` : "—"}
                    </td>

                    <td className="px-5 py-3 text-slate-700">
                      {safeStr(vehicleRegNo) || "—"}
                    </td>

                    <td className="px-5 py-3 text-slate-700">{driverCell}</td>

                    <td className="px-5 py-3 text-slate-700">{schedule}</td>

                    <td className="px-5 py-3 text-slate-700">{dt(updatedAt)}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <PaginationFooter
        page={page}
        totalPages={totalPages}
        totalElements={totalElements}
        size={size}
        onSize={(s) => {
          setPage(0);
          setSize(s);
        }}
        onPrev={() => setPage((p) => Math.max(0, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />

      {/* Ride Drawer */}
      {openRideId && (
        <div className="fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setOpenRideId(null)}
          />

          {/* Wider drawer */}
          <div className="absolute right-0 top-0 h-full w-full sm:w-[820px] bg-white shadow-xl border-l border-slate-200 flex flex-col">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Ride #{openRideId}
                </div>
                <div className="text-xs text-slate-500">
                  Owner support • ride detail
                </div>
              </div>
              <div className="flex items-center gap-2">
                {detail?.ride?.bookingId ? (
                  <button
                    className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
                    onClick={() => setOpenBookingId(detail.ride.bookingId)}
                  >
                    Open booking #{detail.ride.bookingId}
                  </button>
                ) : null}
                <button
                  className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
                  onClick={() => setOpenRideId(null)}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="p-5 overflow-auto">
              {detailLoading && <div className="text-slate-500">Loading detail…</div>}

              {!detailLoading && detail?.ride && (
                <>
                  {/* Summary grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg border border-slate-200">
                      <div className="text-xs text-slate-500">Ride status</div>
                      <div className="mt-1">
                        <Chip tone={toneForRideStatus(detail.ride.status)}>
                          {detail.ride.status || "—"}
                        </Chip>
                      </div>
                    </div>

                    <div className="p-3 rounded-lg border border-slate-200">
                      <div className="text-xs text-slate-500">Booking</div>
                      <div className="mt-1 text-sm text-slate-900">
                        {detail.ride.bookingId ? `#${detail.ride.bookingId}` : "—"}
                      </div>
                      <div className="mt-1 text-xs text-slate-600">
                        Booking status:{" "}
                        <span className="text-slate-800">
                          {detail.ride.bookingStatus || "—"}
                        </span>{" "}
                        • Payment:{" "}
                        <span className="text-slate-800">
                          {detail.ride.bookingPaymentStatus || "—"}
                        </span>
                      </div>
                    </div>

                    <KV label="Vehicle" value={detail.ride.vehicleRegNo || "—"} />

                    <KV
                      label="Driver"
                      value={
                        detail.ride.driverName
                          ? `${detail.ride.driverName}${detail.ride.driverPhone ? ` (${detail.ride.driverPhone})` : ""}`
                          : "—"
                      }
                    />

                    <KV label="Arrived at" value={dt(detail.ride.arrivedAt)} />
                    <KV label="Start time" value={dt(detail.ride.startTime)} />
                    <KV label="End time" value={dt(detail.ride.endTime)} />
                    <KV label="Updated" value={dt(detail.ride.updatedAt)} />
                  </div>

                  {/* Schedule + route */}
                  <Section title="Route & schedule" subtitle="Pickup and drop are formatted consistently">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <KV label="Pickup" value={detail.ride.pickupLocation || "—"} />
                      <KV label="Destination" value={detail.ride.destination || "—"} />

                      <KV
                        label="Pickup time"
                        value={
                          detail.ride.pickupAt
                            ? dt(detail.ride.pickupAt)
                            : `${safeStr(detail.ride.pickupDate) || "—"} ${safeStr(detail.ride.pickupTime) || ""}`.trim()
                        }
                      />
                      <KV
                        label="Drop time"
                        value={
                          detail.ride.dropAt
                            ? dt(detail.ride.dropAt)
                            : `${safeStr(detail.ride.dropDate) || "—"} ${safeStr(detail.ride.dropTime) || ""}`.trim()
                        }
                      />
                    </div>
                  </Section>

                  {/* User / Driver minis */}
                  <Section title="People">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <KV
                        label="User"
                        value={
                          detail.user?.phone || detail.user?.email
                            ? `${safeStr(detail.user?.name) || "—"} • ${safeStr(detail.user?.phone) || "—"} • ${safeStr(detail.user?.email) || "—"}`
                            : "—"
                        }
                      />
                      <KV
                        label="Driver details"
                        value={
                          detail.driver?.driverId
                            ? `#${detail.driver.driverId} • ${safeStr(detail.driver.name) || "—"} • ${safeStr(detail.driver.phone) || "—"}`
                            : "—"
                        }
                      />
                    </div>
                  </Section>

                  {/* Booking snapshot if provided */}
                  {detail.booking ? (
                    <Section title="Booking snapshot">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg border border-slate-200">
                          <div className="text-xs text-slate-500">Booking status</div>
                          <div className="mt-1">
                            <Chip tone={toneForBookingStatus(detail.booking.status)}>
                              {detail.booking.status || "—"}
                            </Chip>
                          </div>
                        </div>

                        <div className="p-3 rounded-lg border border-slate-200">
                          <div className="text-xs text-slate-500">Payment status</div>
                          <div className="mt-1">
                            <Chip tone={toneForPaymentStatus(detail.booking.paymentStatus)}>
                              {detail.booking.paymentStatus || "—"}
                            </Chip>
                          </div>
                        </div>

                        <KV label="Vehicle" value={detail.booking.vehicleRegNo || "—"} />
                        <KV
                          label="User"
                          value={
                            detail.booking.userPhone || detail.booking.userEmail
                              ? `${detail.booking.userPhone || "—"} • ${detail.booking.userEmail || "—"}`
                              : "—"
                          }
                        />
                      </div>
                    </Section>
                  ) : null}

                  {/* Request + Offer + Events if provided */}
                  {detail.request ? (
                    <Section title="Match request">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <KV label="Request" value={detail.request.requestId ? `#${detail.request.requestId}` : "—"} />
                        <KV label="Request status" value={detail.request.status || "—"} />
                        <KV label="Payment status" value={detail.request.paymentStatus || "—"} />
                        <KV label="Batch no" value={detail.request.matchBatchNo ?? "—"} />
                      </div>
                    </Section>
                  ) : null}

                  {detail.offer ? (
                    <Section title="Accepted offer">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <KV label="Offer" value={detail.offer.matchId ? `#${detail.offer.matchId}` : "—"} />
                        <KV label="Offer status" value={detail.offer.status || "—"} />
                        <KV label="Offer batch" value={detail.offer.batchNo ?? "—"} />
                        <KV label="Vehicle" value={detail.offer.vehicleRegNo || "—"} />
                        <KV
                          label="Selected driver"
                          value={
                            detail.offer.selectedDriverId
                              ? `#${detail.offer.selectedDriverId} • ${detail.offer.selectedDriverName || "—"} • ${detail.offer.selectedDriverPhone || "—"}`
                              : "—"
                          }
                        />
                        <KV label="Selected driver at" value={dt(detail.offer.selectedDriverAt)} />
                      </div>
                    </Section>
                  ) : null}

                  {Array.isArray(detail.requestEvents) ? (
                    <Section title="Request timeline" subtitle="Latest events (up to 50)">
                      {detail.requestEvents.length === 0 ? (
                        <div className="text-sm text-slate-500">No events.</div>
                      ) : (
                        <div className="space-y-2">
                          {detail.requestEvents.map((e) => (
                            <div
                              key={e.id ?? `${e.createdAt}-${e.eventType}`}
                              className="p-3 rounded-lg border border-slate-200"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="text-sm text-slate-900">
                                  {safeStr(e.eventType) || "EVENT"}
                                  <div className="mt-1 text-xs text-slate-600">
                                    {safeStr(e.message) || "—"}
                                  </div>
                                </div>
                                <div className="text-xs text-slate-500">{dt(e.createdAt)}</div>
                              </div>
                              <div className="mt-2 text-xs text-slate-500">
                                Actor: {safeStr(e.actorType) || "—"} {e.actorId != null ? `#${e.actorId}` : ""}{" "}
                                {e.traceId ? (
                                  <>
                                    <span className="text-slate-300">•</span> Trace: {e.traceId}
                                  </>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Section>
                  ) : null}

                  <div className="mt-6 text-xs text-slate-500">
                    Created: {dt(detail.ride.createdAt)} • Updated: {dt(detail.ride.updatedAt)}
                  </div>
                </>
              )}

              {!detailLoading && !detail?.ride && (
                <div className="text-sm text-slate-500">No detail found.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Optional booking detail drawer (nested) */}
      {openBookingId && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpenBookingId(null)}
          />
          <div className="absolute right-0 top-0 h-full w-full sm:w-[880px] bg-white shadow-xl border-l border-slate-200 flex flex-col">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Booking #{openBookingId}
                </div>
                <div className="text-xs text-slate-500">Admin support • booking detail</div>
              </div>
              <button
                className="h-10 px-4 rounded-md border border-slate-200 text-sm font-semibold hover:bg-slate-50"
                onClick={() => setOpenBookingId(null)}
              >
                Close
              </button>
            </div>

            <div className="p-5 overflow-auto">
              {bookingDetailLoading && <div className="text-slate-500">Loading booking…</div>}
              {!bookingDetailLoading && bookingDetail?.booking && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <KV label="Booking status" value={bookingDetail.booking.bookingStatus || "—"} />
                  <KV label="Payment status" value={bookingDetail.booking.paymentStatus || "—"} />
                  <KV label="Vehicle" value={bookingDetail.booking.vehicleRegNo || "—"} />
                  <KV label="Driver" value={bookingDetail.booking.driverName || "—"} />
                  <KV label="Pickup" value={bookingDetail.booking.pickupLocation || "—"} />
                  <KV label="Destination" value={bookingDetail.booking.destination || "—"} />
                  <KV
                    label="Pickup time"
                    value={`${safeStr(bookingDetail.booking.pickupDate) || "—"} ${safeStr(bookingDetail.booking.pickupTime) || ""}`.trim()}
                  />
                  <KV
                    label="Drop time"
                    value={`${safeStr(bookingDetail.booking.dropDate) || "—"} ${safeStr(bookingDetail.booking.dropTime) || ""}`.trim()}
                  />
                </div>
              )}
              {!bookingDetailLoading && !bookingDetail?.booking && (
                <div className="text-slate-500">No booking detail found.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
